/**
 * Utility functions for the Commercial PDP Hero Section.
 */

/**
 * Formats a square footage value with comma-separated thousands and " SF" suffix.
 * @param {number|string|null|undefined} value - Square footage value
 * @returns {string|null} Formatted string (e.g., "120,000 SF") or null if invalid
 */
export const formatSquareFeet = (value) => {
  if (value == null || value === '') return null;
  // If value is already a formatted string (e.g., "1,000 Sq. Ft."), return it as-is
  if (typeof value === 'string' && (value.toLowerCase().includes('sq') || value.toLowerCase().includes('ft'))) {
    return value;
  }
  const num = Number(value);
  if (isNaN(num)) return null;
  return `${Math.floor(num).toLocaleString('en-US')} SF`;
};

/**
 * Formats a tenant count value as integer + " Tenants".
 * @param {number|string|null|undefined} value - Tenant count
 * @returns {string|null} Formatted string (e.g., "12 Tenants") or null if invalid
 */
export const formatTenantCount = (value) => {
  const num = Number(value);
  if (value == null || value === '' || isNaN(num)) return null;
  return `${Math.floor(num)} Tenants`;
};

/**
 * Formats an occupancy percentage value as number + "% Occupied".
 * @param {number|string|null|undefined} value - Occupancy percentage (0-100)
 * @returns {string|null} Formatted string (e.g., "95% Occupied") or null if invalid
 */
export const formatOccupancy = (value) => {
  const num = Number(value);
  if (value == null || value === '' || isNaN(num)) return null;
  return `${num}% Occupied`;
};

/**
 * Formats a currency amount with "$" prefix and comma-separated thousands.
 * @param {number|string|null|undefined} amount - Dollar amount
 * @returns {string|null} Formatted string (e.g., "$1,979,200") or null if invalid
 */
export const formatCurrency = (amount) => {
  const num = Number(amount);
  if (amount == null || amount === '' || isNaN(num)) return null;
  return `$${Math.floor(num).toLocaleString('en-US')}`;
};

/**
 * Converts milliseconds remaining into a countdown object with zero-padded strings.
 * @param {number} ms - Milliseconds remaining
 * @returns {{ days: string, hours: string, minutes: string, seconds: string }} Zero-padded countdown segments
 */
export const formatCountdown = (ms) => {
  if (ms == null || isNaN(ms) || ms < 0) {
    return { days: '00', hours: '00', minutes: '00', seconds: '00' };
  }

  const totalSeconds = Math.floor(ms / 1000);
  const days = Math.floor(totalSeconds / 86400);
  const hours = Math.floor((totalSeconds % 86400) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  return {
    days: String(days).padStart(2, '0'),
    hours: String(hours).padStart(2, '0'),
    minutes: String(minutes).padStart(2, '0'),
    seconds: String(seconds).padStart(2, '0'),
  };
};

/**
 * Formats a lot size value with its unit label.
 * Handles both sq ft ("Sq. Ft." → "SF") and acre ("Acre" / "Acres") units.
 * @param {number|string|null|undefined} value - Lot size value
 * @param {string|null|undefined} unit - Unit string (e.g. "Sq. Ft.", "Acre", "Acres")
 * @returns {string|null} Formatted string (e.g. "43,560 SF" or "1.5 Acres") or null if invalid
 */
export const formatLotSize = (value, unit) => {
  if (value == null || value === '') return null;
  const num = Number(String(value).replace(/,/g, ''));
  if (isNaN(num) || num === 0) return null;

  // Use the raw string value to preserve all decimal places as the API sends them
  const rawStr = String(value).replace(/,/g, '');

  // Capitalise first letter of unit if it starts with "acre" (e.g. "acre" → "Acre")
  const unitStr = unit || 'Sq. Ft.';
  const unitLower = unitStr.toLowerCase().trim();
  const unitLabel = unitLower.startsWith('acre')
    ? unitStr.charAt(0).toUpperCase() + unitStr.slice(1)
    : unitStr;

  return `${rawStr} ${unitLabel}`;
};

/**
 * Builds an array of formatted non-null commercial metrics with type metadata.
 * For Commercial Land properties (isLand=true), shows lot size instead of building sq ft.
 * @param {number|string|null|undefined} squareFeet - Square footage
 * @param {number|string|null|undefined} tenantCount - Tenant count
 * @param {number|string|null|undefined} occupancyPercentage - Occupancy percentage
 * @param {boolean} [isLand] - True when the property type is Land (regardless of subType)
 * @param {number|string|null|undefined} [lotSize] - Lot size value (used when isLand=true)
 * @param {string|null|undefined} [lotSizeUnit] - Lot size unit (e.g. "Sq. Ft.", "Acre")
 * @returns {{ type: string, value: string }[]} Array of metric objects (only non-null values)
 */
export const buildMetricsRow = (squareFeet, tenantCount, occupancyPercentage, isLand = false, lotSize = null, lotSizeUnit = null) => {
  const metrics = [];

  if (isLand) {
    const lot = formatLotSize(lotSize, lotSizeUnit);
    if (lot) metrics.push({ type: 'sqft', value: lot });
  } else {
    const sqft = formatSquareFeet(squareFeet);
    if (sqft) metrics.push({ type: 'sqft', value: sqft });
  }

  const tenants = formatTenantCount(tenantCount);
  if (tenants) metrics.push({ type: 'tenants', value: tenants });

  const occupancy = formatOccupancy(occupancyPercentage);
  if (occupancy) metrics.push({ type: 'occupancy', value: occupancy });

  return metrics;
};

/**
 * Builds a pipe-delimited analytics eventLabel string.
 * Format: {propertyId}|{listingId}|{streetNumber} {streetName}, {county}, {state}, {city}, {zip}
 * @param {string} propertyId - Property ID
 * @param {string} listingId - Listing ID
 * @param {object} address - Address object with streetNumber, streetName, county, state, city, zip
 * @returns {string|null} Pipe-delimited eventLabel string, or null if required data is missing
 */
export const buildAnalyticsLabel = (propertyId, listingId, address) => {
  if (!propertyId || !listingId || !address) return null;

  const { streetNumber, streetName, county, state, city, zip } = address;

  return `${propertyId}|${listingId}|${streetNumber} ${streetName}, ${county}, ${state}, ${city}, ${zip}`;
};
